import React, { useEffect, useState } from "react";
import { ColumnProps } from "antd/lib/table";
import { Button, message, Modal } from "antd";
import { EwaybillConsumedMonthlyStatistic, getMonthlyStatistics } from "@/services/api/details";
import {
  SharedEwaybillAccount,
  SharedEwaybillBranchAccount,
  SharedEwaybillShippingAddress,
  SharedEwaybillShippingAddressStatus,
  SharedEwaybillSubscription,
} from "@/services/api/sharedEwaybillAccount";
import { Link, useHistory } from "react-router-dom";
import SharedEwaybillAccountTypeNameMap from "@/map/SharedEwaybillAccountTypeNameMap";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import { useModalStore } from "@/hooks/Modal/hooks";
import AddCountModal, { AddCountModalProps } from "../components/AddCountModal";
import SubtractCountModal, { SubtractCountModalProps } from "../components/SubtractCountModal";
import {
  deleteSharedEwaybillShippingAddress,
  disableSharedEwaybillShippingAddress,
  enableSharedEwaybillShippingAddress,
} from "@/services/api/sharedEwaybillShippingAddress";
import ChangeNickNameModal, { ChangeNickNameModalProps } from "../components/ChangeNickNameModal";
import moment from "moment";
import SharedEwaybillAccountType from "@/enum/SharedEwaybillAccountType";
import RecycleSettingButton from "../components/RecycleSettingButton";
import LogButton from "../components/LogButton";

interface UseProviderTableColumnsProps {
  ewaybillShippingAddressMapping?: Map<number, SharedEwaybillShippingAddress>;
  ewaybillAccountMapping?: Map<number, SharedEwaybillAccount>;
  branchAccountMapping?: Map<number, SharedEwaybillBranchAccount>;
  subscriptionMapping?: Map<number, SharedEwaybillSubscription>;

  data?: SharedEwaybillShippingAddress[];

  reload?(): void;
}

function useProviderTableColumns(props: UseProviderTableColumnsProps): ColumnProps<SharedEwaybillShippingAddress>[] {
  const {
    ewaybillAccountMapping,
    branchAccountMapping,
    ewaybillShippingAddressMapping,
    subscriptionMapping,
    data: list,
    reload,
  } = props;

  const [statisticsColumns, setStatisticsColumns] = useState<ColumnProps<SharedEwaybillShippingAddress>[]>([
    { title: "加载中", width: 70 },
    { title: "加载中", width: 70 },
    { title: "加载中", width: 70 },
  ]);

  const modalStore = useModalStore();
  const history = useHistory();

  useEffect(() => {
    (async () => {
      const sharedEwaybillShippingAddressIds: number[] = [];

      list?.forEach((it) => {
        if (it?.id) {
          sharedEwaybillShippingAddressIds.push(it?.id);
        }
      });

      if (sharedEwaybillShippingAddressIds.length) {
        const data = await getMonthlyStatistics(sharedEwaybillShippingAddressIds);

        /**
         * `year-month-shippingAddressId` -> EwaybillConsumedMonthlyStatistic 索引
         */
        const shippingAddressYearMonthMapping = new Map<string, EwaybillConsumedMonthlyStatistic>();
        const yearMonths = [];
        data.forEach((it) => {
          const yearMonth = `${it.year}-${it.month.toString().padStart(2, "0")}`;
          const shippingAddressYearMonth = `${yearMonth}-${it.sharedEwaybillShippingAddressId}`;
          if (!yearMonths.includes(yearMonth)) {
            yearMonths.push(yearMonth);
          }
          shippingAddressYearMonthMapping.set(shippingAddressYearMonth, it);
        });

        yearMonths.sort((a, b) => {
          return moment(a).unix() - moment(b).unix();
        });

        const newStatisticsColumns: ColumnProps<any>[] = yearMonths.map((yearMonth) => {
          return {
            key: yearMonth,
            title: yearMonth,
            width: 150,
            render(_, shippingAddress) {
              const id = shippingAddress.id;
              const shippingAddressYearMonth = `${yearMonth}-${id}`;
              const statistic = shippingAddressYearMonthMapping.get(shippingAddressYearMonth);

              if (!statistic) {
                return null;
              }
              return (
                <div style={{ fontSize: 11, color: "black" }}>
                  <div>已用：{statistic.allocatedQuantity}</div>
                  <div>已取消：{statistic.cancelQuantity}</div>
                </div>
              );
            },
          };
        });

        setStatisticsColumns(newStatisticsColumns);
      }
    })();
  }, [props.data]);

  return [
    {
      title: "我的面单账号",
      key: "platformUsername",
      width: 230,
      render: (_, shippingAddress) => {
        const ewaybillAccount = ewaybillAccountMapping.get(shippingAddress.id);

        if (!ewaybillAccount) return null;

        const ewaybillAccountType = ewaybillAccount.type;

        const ewaybillTypeName = SharedEwaybillAccountTypeNameMap[ewaybillAccountType];

        const showOriginalName =
          ewaybillAccountType === SharedEwaybillAccountType.PDD_AUTH &&
          ewaybillAccount.name !== ewaybillAccount.platformUsername;

        const ewaybillShippingAddress = ewaybillShippingAddressMapping.get(shippingAddress.id);

        return (
          <>
            <div className="text-small">
              <div>
                <span>{ewaybillAccount?.name ?? ewaybillAccount?.platformUsername}</span>

                {showOriginalName ? (
                  <span className="ml5" style={{ color: "#777", fontSize: "85%" }}>
                    ({ewaybillAccount.platformUsername})
                  </span>
                ) : null}
              </div>
              <div>{ewaybillTypeName}</div>
              {ewaybillShippingAddress ? <div>ID:{ewaybillShippingAddress.accessToken}</div> : null}
            </div>
          </>
        );
      },
    },
    {
      title: "快递公司/网点/发货地址",
      key: "logisticsCompany",
      width: 400,
      render: (_, shippingAddress) => {
        const subscription = subscriptionMapping.get(shippingAddress.id);
        const branchAccount = branchAccountMapping.get(shippingAddress.id);
        const { province, city, district, town, street } = shippingAddress;

        const logisticsCompany = subscription?.logisticsCompany;
        const logisticsCompanyName = LogisticsCompanyNameMap[logisticsCompany];

        const branchAccountNameArr = [];
        if (branchAccount.branchName) {
          branchAccountNameArr.push(branchAccount.branchName);
        }
        if (branchAccount.branchCode) {
          branchAccountNameArr.push(`(${branchAccount.branchCode})`);
        }
        if (branchAccount.waybillNoTypeName) {
          branchAccountNameArr.push(`-${branchAccount.waybillNoTypeName}`);
        }

        const branchAccountName = branchAccountNameArr.join("");
        const shippingAddressName = [province, city, district, town, street].filter((it) => it).join("");

        return (
          <div className="text-small">{[logisticsCompanyName, branchAccountName, shippingAddressName].join("/")}</div>
        );
      },
    },
    {
      title: "备注/使用面单的人",
      key: "consumerCompanyName",
      width: 150,
      render: (_, shippingAddress) => {
        const { consumerName, id } = shippingAddress;
        const account = ewaybillAccountMapping.get(shippingAddress.id);
        const consumerCompanyName = account?.consumerCompanyName;

        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
              <span className="fw700">{consumerName}</span>
              <Button
                type="link"
                size="small"
                onClick={() => {
                  modalStore.openModal<ChangeNickNameModalProps>(ChangeNickNameModal, {
                    shippingAddress,
                    reload,
                  });
                }}
              >
                修改
              </Button>
            </div>

            <div className={"attentionColor"}>微信：{consumerCompanyName}</div>
          </div>
        );
      },
    },
    {
      title: "共享给别人的余额",
      key: "quantity",
      width: 150,
      render: (_, shippingAddress) => {
        const { quantity, id, status } = shippingAddress;

        return (
          <div className="flex" style={{ alignItems: "center" }}>
            <span style={{ minWidth: 40 }}>{quantity}</span>

            {status !== SharedEwaybillShippingAddressStatus.DELETED ? (
              <div className={"inlineBlock"}>
                <div>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      modalStore.openModal<AddCountModalProps>(AddCountModal, {
                        shippingAddressId: id,
                        reload: reload,
                      });
                    }}
                  >
                    增加单数
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      modalStore.openModal<SubtractCountModalProps>(SubtractCountModal, {
                        shippingAddressId: id,
                        reload: reload,
                      });
                    }}
                  >
                    减少单数
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: (
        <span>
          <span className="mr5">已用单数</span>
          <Link to="/shareEwaybillToOthers/details/all/all/all/all">总明细</Link>
        </span>
      ),
      key: "used",
      width: 180,
      render: (_, shippingAddress) => {
        const { allocatedQuantity, cancelQuantity } = shippingAddress;

        const ewaybillAccount = ewaybillAccountMapping.get(shippingAddress.id);
        const subscription = subscriptionMapping.get(shippingAddress.id);

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <div style={{ minWidth: 80 }} className="tl">
              <div>已用：{allocatedQuantity}</div>
              <div>已取消：{cancelQuantity}</div>
            </div>
            <Link
              to={`/shareEwaybillToOthers/details/${ewaybillAccount.ewaybillAccountId}/${ewaybillAccount.consumerCompanyId}/${subscription.logisticsCompany}/${shippingAddress.id}`}
            >
              明细
            </Link>
          </div>
        );
      },
    },
    ...statisticsColumns,
    {
      title: "状态",
      width: 180,
      key: "status",
      render(_, shippingAddress) {
        if (shippingAddress.status === SharedEwaybillShippingAddressStatus.DELETED) {
          return (
            <span className="text-small">
              <span
                style={{ display: "inline-block", width: 7, height: 7, borderRadius: "100%", background: "#F5222D" }}
              ></span>
              <span className="ml5">已删除</span>
            </span>
          );
        }

        if (shippingAddress.status === SharedEwaybillShippingAddressStatus.DISABLE) {
          return (
            <span className="text-small">
              <span
                style={{ display: "inline-block", width: 7, height: 7, borderRadius: "100%", background: "#BFBFBF" }}
              ></span>
              <span className="ml5">暂停</span>
            </span>
          );
        }

        if (shippingAddress.status === SharedEwaybillShippingAddressStatus.ENABLE) {
          return (
            <span className="text-small">
              <span
                style={{ display: "inline-block", width: 7, height: 7, borderRadius: "100%", background: "#52C41A" }}
              ></span>
              <span className="ml5">共享中</span>
            </span>
          );
        }

        return null;
      },
    },
    {
      title: "操作",
      key: "operation",
      width: 150,
      render: (_, shippingAddress) => {
        const { id, status, consumerName, doNotIncreaseQuantityWhenRecycle } = shippingAddress;

        return (
          <div>
            {status === SharedEwaybillShippingAddressStatus.ENABLE ? (
              <Button
                type="link"
                size="small"
                onClick={() => {
                  Modal.confirm({
                    title: "您确定要暂停共享吗",
                    content: (
                      <div className="text-error">
                        暂停共享后店铺不能再使用该网点的单号，<p style={{ color: "#999999" }}>您还要继续吗？</p>
                      </div>
                    ),
                    async onOk() {
                      let result = await disableSharedEwaybillShippingAddress(id);

                      if (result?.successful) {
                        message.success("暂停共享成功！");

                        reload?.();
                      }
                    },
                  });
                }}
              >
                暂停共享
              </Button>
            ) : null}

            {status === SharedEwaybillShippingAddressStatus.DISABLE ? (
              <Button
                type="link"
                size="small"
                onClick={() => {
                  Modal.confirm({
                    title: "您确定要恢复共享吗",
                    content: (
                      <div className="text-error">
                        恢复共享后店铺可以再次使用该网点的单号，<p style={{ color: "#999999" }}>您还要继续吗？</p>
                      </div>
                    ),
                    async onOk() {
                      let result = await enableSharedEwaybillShippingAddress(id);

                      if (result?.successful) {
                        message.success("恢复共享成功！");

                        reload?.();
                      }
                    },
                  });
                }}
              >
                恢复共享
              </Button>
            ) : null}

            <Button
              type="link"
              size="small"
              onClick={() => {
                const { province, city, district, town, street } = shippingAddress;
                const account = ewaybillAccountMapping.get(shippingAddress.id);
                const subscription = subscriptionMapping.get(shippingAddress.id);
                const branchAccount = branchAccountMapping.get(shippingAddress.id);
                const name = account?.name ?? account?.platformUsername;
                const consumerCompanyName = account?.consumerCompanyName;
                const type = account?.type;

                const ewaybillTypeName = SharedEwaybillAccountTypeNameMap[type];
                const logisticsCompanyName = LogisticsCompanyNameMap[subscription.logisticsCompany];
                const branchCode = branchAccount.branchCode;
                const joinedShippingAddress = [province, city, district, town, street].filter((it) => it).join("");

                const title = [
                  name,
                  `(${ewaybillTypeName})`,
                  logisticsCompanyName,
                  branchCode,
                  joinedShippingAddress,
                ].join("/");

                const search = new URLSearchParams();

                search.set(
                  "extraInfo",
                  JSON.stringify({
                    title: title,
                    // 备注
                    consumerName,
                    // 微信
                    consumerCompanyName,
                  })
                );

                history.push(`/shareEwaybillToOthers/operationRecord/${id}?${search.toString()}`);
              }}
            >
              充值记录
            </Button>

            {status === SharedEwaybillShippingAddressStatus.DISABLE ? (
              <Button
                type="link"
                size="small"
                onClick={() => {
                  Modal.confirm({
                    title: "是否确认删除面单共享?",
                    okButtonProps: {
                      danger: true,
                    },
                    okText: "删除",
                    async onOk() {
                      let result = await deleteSharedEwaybillShippingAddress(id);

                      if (result?.successful) {
                        message.success("删除面单共享成功！");

                        reload?.();
                      }
                    },
                  });
                }}
              >
                删除共享
              </Button>
            ) : null}

            <RecycleSettingButton
              shippingAddressId={id}
              onRefresh={reload}
              doNotIncreaseQuantityWhenRecycle={doNotIncreaseQuantityWhenRecycle}
            />

            <LogButton shippingAddressId={id} />
          </div>
        );
      },
    },
  ];
}

export default useProviderTableColumns;
